import { cn, useDebounce } from '@workspace/utils';
import { useEffect, useMemo, useState } from 'react';
import type { Path } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';
import { FieldCombobox, FieldInput, type ComboboxOption } from '.';
import { useCheckBirth } from '@workspace/api';

export interface FieldBirthplaceSelectProps<TFormValues extends Record<string, any> = any> {
  nameCountry: Path<TFormValues> | string;
  nameBirthplace: Path<TFormValues> | string;
  nameDepartment?: Path<TFormValues> | string;
  disabled?: boolean;
  required?: boolean;
}

interface BirthplaceResult {
  sBirthplace: string;
  sInsee: string;
}

const BIRTHPLACE_CONFIG = {
  minQueryLength: 2,
  debounceDelay: 200,
  placeholder: 'Tapez au moins 2 lettres pour rechercher',
  emptyMessage: 'Aucune ville trouvée.',
  loadingMessage: 'Recherche en cours...',
} as const;

export function FieldBirthplaceSelect({ nameCountry, nameBirthplace, nameDepartment, disabled = false, required = true }: FieldBirthplaceSelectProps) {
  const methods = useFormContext();
  const [searchQuery, setSearchQuery] = useState('');
  const [manualMode, setManualMode] = useState(false);
  const debouncedQuery = useDebounce(searchQuery, BIRTHPLACE_CONFIG.debounceDelay);
  const birthplaceQuery = useCheckBirth(debouncedQuery);

  const countryValue = methods.watch(nameCountry);

  // Vérifier si le pays sélectionné est la France (code ISO "FRA")
  const isFrance = useMemo(() => {
    if (!countryValue) return false;

    // Vérifier si c'est le code ISO "FRA" ou le nom "FRANCE" (rétrocompatibilité)
    const value = countryValue.toString().toUpperCase();
    return value === 'FRA' || value === 'FRANCE';
  }, [countryValue]);

  // Valeurs courantes des champs
  const currentBirthplace = methods.watch(nameBirthplace);

  // Bascule en mode manuel si l'API échoue
  useEffect(() => {
    if (birthplaceQuery.isError) setManualMode(true);
  }, [birthplaceQuery.isError]);

  // Options de recherche
  const options: ComboboxOption<BirthplaceResult>[] = useMemo(() => {
    if (!isFrance || !Array.isArray(birthplaceQuery.data) || birthplaceQuery.isError) return [];

    return birthplaceQuery.data.map((item) => ({
      value: `${item.sBirthplace}-${item.sInsee}`, // Valeur unique
      label: `${item.sBirthplace.toUpperCase()}`,
      data: {
        sBirthplace: item.sBirthplace,
        sInsee: item.sInsee,
      },
    }));
  }, [birthplaceQuery.data, birthplaceQuery.isError, isFrance]);

  // Handler de sélection : remplit tous les champs
  const handleBirthplaceSelection = (option: ComboboxOption<BirthplaceResult> | null) => {
    if (!option?.data) return;
    const { sBirthplace, sInsee } = option.data;
    methods.setValue(nameBirthplace, sBirthplace.toUpperCase(), { shouldValidate: true });

    // Enregistrer le département si le nom du champ est fourni et si on a un code INSEE
    if (nameDepartment && sInsee) {
      methods.setValue(nameDepartment, sInsee);
    }
  };

  // Init search avec valeurs existantes
  useEffect(() => {
    if (isFrance && currentBirthplace && !searchQuery) {
      setSearchQuery(`${currentBirthplace}`);
    }
  }, [currentBirthplace, searchQuery, isFrance]);

  // Mode saisie libre pour les pays autres que la France
  if (!isFrance) {
    return (
      <FieldInput
        label="Lieu de naissance"
        name={nameBirthplace}
        required={required}
        disabled={disabled}
        placeholder="Ex: Paris"
        normalize="uppercase"
        validation={{
            required: required ? 'Le lieu de naissance est requis' : false,
        }}
      />
    );
  }

  return (
    <div className={cn('grid gap-4 w-full', !manualMode && 'md:grid-cols-2')}>
      {!manualMode && (
        <FieldCombobox
          label="Rechercher un lieu de naissance"
          name={`${nameBirthplace}-search`}
          options={options}
          required={false}
          disabled={disabled}
          placeholder={BIRTHPLACE_CONFIG.placeholder}
          loading={birthplaceQuery.isFetching}
          loadingMessage={BIRTHPLACE_CONFIG.loadingMessage}
          emptyMessage={BIRTHPLACE_CONFIG.emptyMessage}
          minQueryLength={BIRTHPLACE_CONFIG.minQueryLength}
          filterOptions={false}
          onInputChange={setSearchQuery}
          onChange={handleBirthplaceSelection}
        />
      )}

      <FieldInput
        label="Lieu de naissance"
        name={nameBirthplace}
        disabled={!manualMode}
        required={required}
        placeholder="Ex: NANCY"
        normalize="uppercase"
        validation={{
            required: required ? 'Le lieu de naissance est requis' : false,
        }}
      />
    </div>
  );
}
